import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { Phone, Mail, MapPin, Home, DollarSign, Calendar, Pencil, Trash2, X, Save, Loader2, Link } from "lucide-react";
import { DealTrackerLead, DEAL_STAGES, LEAD_SOURCES, DealStage } from "@/types/dealTracker";
import DealStageBar from "./DealStageBar";
import DealActivityFeed from "./DealActivityFeed";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LeadDetailModalProps {
  lead: DealTrackerLead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (data: Partial<DealTrackerLead> & { id: string }) => Promise<void>;
  onStageChange: (id: string, stage: DealStage) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

const LeadDetailModal = ({ 
  lead, 
  open, 
  onOpenChange, 
  onUpdate,
  onStageChange,
  onDelete,
  isUpdating,
  isDeleting
}: LeadDetailModalProps) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editData, setEditData] = useState<Partial<DealTrackerLead>>({});

  // Fetch seller's properties for the link dropdown (must be before early return)
  const { data: properties = [] } = useQuery({
    queryKey: ['seller-properties-list', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('properties')
        .select('id, address, city, state')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && open,
  });
  
  if (!lead) return null;
  
  const currentStage = DEAL_STAGES.find(s => s.value === lead.deal_stage);

  // Find the linked property for view mode
  const linkedProperty = lead.property_id
    ? properties.find(p => p.id === lead.property_id)
    : null;
  
  const handleEdit = () => {
    setEditData({
      seller_names: lead.seller_names,
      phone_number: lead.phone_number || '',
      email: lead.email || '',
      mailing_address: lead.mailing_address || '',
      property_addresses: lead.property_addresses || '',
      expected_commission: lead.expected_commission || '',
      lead_source: lead.lead_source,
      notes: lead.notes || '',
      property_id: lead.property_id || null,
    });
    setIsEditing(true);
  };
  
  const handleSave = async () => {
    const saveData = { ...editData };
    // Convert empty string property_id to null
    if (!saveData.property_id) {
      saveData.property_id = null;
    }
    await onUpdate({ id: lead.id, ...saveData });
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
  };
  
  const handleDelete = async () => {
    await onDelete(lead.id);
    setShowDeleteConfirm(false);
    onOpenChange(false);
  };
  
  const handleStageChange = async (stage: DealStage) => {
    await onStageChange(lead.id, stage);
  };

  const handlePropertyLink = (propertyId: string) => {
    const newId = propertyId === 'none' ? null : propertyId;
    setEditData(prev => ({ ...prev, property_id: newId }));
    // Auto-fill property address if empty
    if (newId && !editData.property_addresses) {
      const prop = properties.find(p => p.id === newId);
      if (prop) {
        setEditData(prev => ({ ...prev, property_addresses: `${prop.address}, ${prop.city}, ${prop.state}` }));
      }
    }
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold">
                {isEditing ? 'Edit Lead' : lead.seller_names}
              </DialogTitle>
              <div className="flex items-center gap-2 mr-8">
                {!isEditing ? (
                  <>
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isUpdating}>
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-1" />
                      )}
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Deal Stage Bar */}
            <Card>
              <CardContent className="pt-6">
                <DealStageBar 
                  currentStage={lead.deal_stage}
                  onStageChange={handleStageChange}
                  disabled={isUpdating}
                />
              </CardContent>
            </Card>
            
            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Seller Name(s)</Label>
                  <Input
                    value={editData.seller_names || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, seller_names: e.target.value }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={editData.phone_number || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, phone_number: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={editData.email || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Mailing Address</Label>
                  <Input
                    value={editData.mailing_address || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, mailing_address: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Property Address(es)</Label>
                  <Textarea
                    value={editData.property_addresses || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, property_addresses: e.target.value }))}
                    rows={2}
                  />
                </div>

                {/* Link to Deal dropdown */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Link className="h-3.5 w-3.5" />
                    Link to Uploaded Deal
                  </Label>
                  <Select
                    value={editData.property_id || 'none'}
                    onValueChange={handlePropertyLink}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a deal to link" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.address}, {prop.city}, {prop.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expected Commission</Label>
                    <Input
                      value={editData.expected_commission || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, expected_commission: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deal Source</Label>
                    <Select
                      value={editData.lead_source}
                      onValueChange={(value) => setEditData(prev => ({ ...prev, lead_source: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="grid gap-4 md:grid-cols-2">
                {/* Contact Information */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {lead.phone_number && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${lead.phone_number}`} className="hover:text-primary">
                          {lead.phone_number}
                        </a>
                      </div>
                    )}
                    {lead.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${lead.email}`} className="hover:text-primary">
                          {lead.email}
                        </a>
                      </div>
                    )}
                    {lead.mailing_address && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span>{lead.mailing_address}</span>
                      </div>
                    )}
                    {!lead.phone_number && !lead.email && !lead.mailing_address && (
                      <p className="text-sm text-muted-foreground">No contact information</p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Property Information */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Property Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {lead.property_addresses ? (
                      <div className="flex items-start gap-2 text-sm">
                        <Home className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="whitespace-pre-line">{lead.property_addresses}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No property addresses</p>
                    )}
                    {linkedProperty ? (
                      <div className="flex items-center gap-2 mt-2">
                        <Link className="h-3.5 w-3.5 text-primary" />
                        <Badge variant="secondary" className="text-xs">
                          Linked: {linkedProperty.address}, {linkedProperty.city}, {linkedProperty.state}
                        </Badge>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">No deal linked</p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Lead Information */}
                <Card className="md:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Lead Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Deal Source</p>
                        <Badge variant="secondary">{lead.lead_source}</Badge>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Current Stage</p>
                        <Badge className={currentStage?.color}>
                          {currentStage?.label}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Expected Commission</p>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {lead.expected_commission || 'Not set'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Created</p>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">
                            {format(new Date(lead.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {lead.notes && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Notes</p>
                        <p className="text-sm whitespace-pre-line">{lead.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activity Feed */}
                <DealActivityFeed leadId={lead.id} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this lead? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LeadDetailModal;
